import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useVehicleImage } from '../hooks/useVehicleImage';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Car, Plus, Trash2, Pencil, ArrowRight, Fuel, Settings2,
  Calendar, Building2, ShieldCheck, Banknote, Tag, ImageIcon,
  Loader2, Eye, X
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_DURATIONS = ['36', '48', '60'];
const DEFAULT_KM = ['10000', '15000', '20000', '25000'];

// Componente Hero per card Offerta del Mese
// Priorità: link_immagine_effective (risolto da backend con fallback Drive) > Drive hook locale
const PromoHeroImage = ({ linkImmagine, marca, modello }) => {
  const { imageUrl: driveUrl } = useVehicleImage(marca, linkImmagine ? null : modello);
  const finalUrl = linkImmagine || driveUrl;
  if (!finalUrl) return null;
  return (
    <div className="w-full h-32 bg-gradient-to-b from-white to-amber-50/30 flex items-center justify-center px-4 py-2">
      <img src={finalUrl} alt={`${marca} ${modello}`} className="max-w-full max-h-full object-contain drop-shadow-md" loading="lazy" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
    </div>
  );
};

const OfferteDelMesePage = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader, impersonatedUser } = useAuth();
  const [offerte, setOfferte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOfferta, setEditingOfferta] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [repartiDisponibili, setRepartiDisponibili] = useState([]);

  const isSuperAdmin = user?.role === 'super_admin';

  const emptyForm = {
    marca: '', modello: '', versione: '', colore: '',
    alimentazione: '', cambio: '', company: '', listino: 'NUOVO',
    data_disponibilita: '', data_immatricolazione: '',
    franchigia_rca: '', franchigia_danni: '', franchigia_furto: '',
    provvigione_fissa: '',
    custom_durations: [...DEFAULT_DURATIONS],
    custom_km_options: [...DEFAULT_KM],
    quotations: {},
    reparti_visibili: [],
    pubblica_catalogo: false,
    link_immagine: '', note: ''
  };
  const [formData, setFormData] = useState(emptyForm);

  const fetchOfferte = useCallback(async () => {
    try {
      const params = {};
      if (impersonatedUser) params.view_as = impersonatedUser.id;
      const response = await axios.get(`${API}/offerte-mese`, {
        headers: getAuthHeader(), params
      });
      setOfferte(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle offerte del mese');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, impersonatedUser]);

  useEffect(() => {
    fetchOfferte();
    if (isSuperAdmin) {
      axios.get(`${API}/modules/reparti-disponibili`, { headers: getAuthHeader() })
        .then(res => setRepartiDisponibili(res.data))
        .catch(() => {});
    }
  }, [fetchOfferte, isSuperAdmin, getAuthHeader]);

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleQuotationChange = (durata, km, value) => {
    const key = `${durata}_${km}`;
    setFormData(prev => {
      const q = { ...prev.quotations };
      if (value === '' || value === null) {
        delete q[key];
      } else {
        q[key] = parseFloat(value) || 0;
      }
      return { ...prev, quotations: q };
    });
  };

  // Gestione intestazioni dinamiche
  const handleDurationChange = (idx, value) => {
    setFormData(prev => {
      const durations = [...prev.custom_durations];
      const oldVal = durations[idx];
      durations[idx] = value;
      // Aggiorna le chiavi delle quotazioni
      const q = { ...prev.quotations };
      prev.custom_km_options.forEach(km => {
        const oldKey = `${oldVal}_${km}`;
        const newKey = `${value}_${km}`;
        if (q[oldKey] !== undefined && oldVal !== value) {
          q[newKey] = q[oldKey];
          delete q[oldKey];
        }
      });
      return { ...prev, custom_durations: durations, quotations: q };
    });
  };

  const handleKmChange = (idx, value) => {
    setFormData(prev => {
      const kms = [...prev.custom_km_options];
      const oldVal = kms[idx];
      kms[idx] = value;
      // Aggiorna le chiavi delle quotazioni
      const q = { ...prev.quotations };
      prev.custom_durations.forEach(dur => {
        const oldKey = `${dur}_${oldVal}`;
        const newKey = `${dur}_${value}`;
        if (q[oldKey] !== undefined && oldVal !== value) {
          q[newKey] = q[oldKey];
          delete q[oldKey];
        }
      });
      return { ...prev, custom_km_options: kms, quotations: q };
    });
  };

  const addDuration = () => {
    setFormData(prev => ({
      ...prev,
      custom_durations: [...prev.custom_durations, '']
    }));
  };

  const removeDuration = (idx) => {
    setFormData(prev => {
      const durations = prev.custom_durations.filter((_, i) => i !== idx);
      const removedVal = prev.custom_durations[idx];
      const q = { ...prev.quotations };
      prev.custom_km_options.forEach(km => { delete q[`${removedVal}_${km}`]; });
      return { ...prev, custom_durations: durations, quotations: q };
    });
  };

  const addKm = () => {
    setFormData(prev => ({
      ...prev,
      custom_km_options: [...prev.custom_km_options, '']
    }));
  };

  const removeKm = (idx) => {
    setFormData(prev => {
      const kms = prev.custom_km_options.filter((_, i) => i !== idx);
      const removedVal = prev.custom_km_options[idx];
      const q = { ...prev.quotations };
      prev.custom_durations.forEach(dur => { delete q[`${dur}_${removedVal}`]; });
      return { ...prev, custom_km_options: kms, quotations: q };
    });
  };

  const handleRepartoToggle = (repartoId) => {
    setFormData(prev => {
      const current = prev.reparti_visibili || [];
      if (current.includes(repartoId)) {
        return { ...prev, reparti_visibili: current.filter(id => id !== repartoId) };
      }
      return { ...prev, reparti_visibili: [...current, repartoId] };
    });
  };

  const openCreate = () => {
    setEditingOfferta(null);
    setFormData(emptyForm);
    setShowForm(true);
  };

  const openEdit = (offerta) => {
    setEditingOfferta(offerta);
    const durations = offerta.custom_durations?.length > 0
      ? offerta.custom_durations
      : DEFAULT_DURATIONS;
    const kms = offerta.custom_km_options?.length > 0
      ? offerta.custom_km_options
      : DEFAULT_KM;
    setFormData({
      marca: offerta.marca || '',
      modello: offerta.modello || '',
      versione: offerta.versione || '',
      colore: offerta.colore || '',
      alimentazione: offerta.alimentazione || '',
      cambio: offerta.cambio || '',
      company: offerta.company || '',
      listino: offerta.listino || 'NUOVO',
      data_disponibilita: offerta.data_disponibilita || '',
      data_immatricolazione: offerta.data_immatricolazione || '',
      franchigia_rca: offerta.franchigia_rca || '',
      franchigia_danni: offerta.franchigia_danni || '',
      franchigia_furto: offerta.franchigia_furto || '',
      provvigione_fissa: offerta.provvigione_fissa || '',
      custom_durations: [...durations],
      custom_km_options: [...kms],
      quotations: offerta.quotations || {},
      reparti_visibili: offerta.reparti_visibili || [],
      pubblica_catalogo: offerta.pubblica_catalogo || false,
      link_immagine: offerta.link_immagine || '',
      note: offerta.note || ''
    });
    setShowForm(true);
  };

  const handleSubmit = async () => {
    if (!formData.marca || !formData.modello) {
      toast.error('Marca e Modello sono obbligatori');
      return;
    }
    const validDurations = formData.custom_durations.filter(d => d.trim() !== '');
    const validKms = formData.custom_km_options.filter(k => k.trim() !== '');
    if (validDurations.length === 0 || validKms.length === 0) {
      toast.error('Definisci almeno una durata e un km per la matrice');
      return;
    }
    if (Object.keys(formData.quotations).length === 0) {
      toast.error('Inserisci almeno una combinazione durata/km');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        provvigione_fissa: parseFloat(formData.provvigione_fissa) || 0,
        custom_durations: validDurations,
        custom_km_options: validKms
      };
      if (editingOfferta) {
        await axios.put(`${API}/offerte-mese/${editingOfferta.id}`, payload, { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } });
        toast.success('Offerta aggiornata');
      } else {
        await axios.post(`${API}/offerte-mese`, payload, { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } });
        toast.success('Offerta creata');
      }
      setShowForm(false);
      fetchOfferte();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (offertaId) => {
    try {
      const res = await axios.delete(`${API}/offerte-mese/${offertaId}`, { headers: getAuthHeader() });
      toast.success(`Offerta eliminata. ${res.data.offerte_aggiornate || 0} offerte collegate aggiornate a VENDUTA.`);
      setDeleteConfirm(null);
      fetchOfferte();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella cancellazione');
    }
  };

  const getMinQuote = (quotations) => {
    if (!quotations || Object.keys(quotations).length === 0) return null;
    let min = Infinity;
    let minKey = '';
    for (const [key, val] of Object.entries(quotations)) {
      if (val > 0 && val < min) {
        min = val;
        minKey = key;
      }
    }
    if (min === Infinity) return null;
    const [durata, km] = minKey.split('_');
    return { canone: min, durata, km };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="offerte-del-mese-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Offerte del Mese
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Offerte promozionali e stock speciali
          </p>
        </div>
        {isSuperAdmin && (
          <Button
            onClick={openCreate}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="btn-nuova-macchina"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuova Macchina
          </Button>
        )}
      </div>

      {/* Cards Grid */}
      {offerte.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Tag className="w-16 h-16 mx-auto text-amber-300 mb-4" />
            <h3 className="text-lg font-semibold text-slate-800">Nessuna offerta del mese</h3>
            <p className="text-slate-500 mt-1">
              {isSuperAdmin ? 'Clicca "Nuova Macchina" per creare la prima offerta.' : 'Non ci sono offerte promozionali al momento.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="offerte-grid">
          {offerte.map(offerta => {
            const minQ = getMinQuote(offerta.quotations);
            return (
              <Card
                key={offerta.id}
                className="group overflow-hidden border-2 border-amber-400 card-hover relative"
                data-testid={`promo-card-${offerta.id}`}
              >
                <CardContent className="p-0">
                  {/* Hero Image (link manuale risolto dal backend o Drive fallback) */}
                  <PromoHeroImage linkImmagine={offerta.link_immagine_effective || offerta.link_immagine} marca={offerta.marca} modello={offerta.modello} />
                  
                  <div className="p-6 pb-4 bg-gradient-to-br from-amber-50 to-amber-100/50 relative">
                    {offerta.company && (
                      <div className="absolute top-2 right-2 z-10">
                        <Badge className="bg-amber-500 text-white text-[10px] px-2 py-0.5 font-bold shadow-md">
                          {offerta.company}
                        </Badge>
                      </div>
                    )}
                    {isSuperAdmin && (
                      <div className="absolute top-2 left-2 z-10 flex gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); openEdit(offerta); }}
                          className="w-8 h-8 rounded-lg bg-white/90 shadow-sm flex items-center justify-center hover:bg-blue-50 transition-colors"
                          title="Modifica"
                          data-testid={`btn-edit-${offerta.id}`}
                        >
                          <Pencil className="w-4 h-4 text-blue-600" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); setDeleteConfirm(offerta); }}
                          className="w-8 h-8 rounded-lg bg-white/90 shadow-sm flex items-center justify-center hover:bg-red-50 transition-colors"
                          title="Elimina"
                          data-testid={`btn-delete-${offerta.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    )}
                    <div className="flex items-start justify-between">
                      <div className={isSuperAdmin ? 'mt-8' : ''}>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-medium text-amber-700">{offerta.marca}</p>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500 text-amber-700">
                            PROMO
                          </Badge>
                        </div>
                        <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                          {offerta.modello}
                        </h3>
                      </div>
                    </div>
                    {offerta.versione && (
                      <p className="text-sm text-slate-600 mt-2 line-clamp-2">{offerta.versione}</p>
                    )}
                  </div>

                  <div className="p-6 pt-4">
                    <div className="flex flex-wrap gap-2 mb-4">
                      {offerta.alimentazione && (
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Fuel className="w-3 h-3" />
                          {offerta.alimentazione}
                        </Badge>
                      )}
                      {offerta.cambio && (
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <Settings2 className="w-3 h-3" />
                          {offerta.cambio}
                        </Badge>
                      )}
                      {offerta.colore && (
                        <Badge variant="outline" className="flex items-center gap-1 border-slate-300">
                          {offerta.colore}
                        </Badge>
                      )}
                      {offerta.listino && (
                        <Badge className="bg-slate-700 text-white text-[10px]">
                          {offerta.listino}
                        </Badge>
                      )}
                    </div>

                    {offerta.data_disponibilita && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mb-4">
                        <Calendar className="w-3 h-3" />
                        Disponibile: {offerta.data_disponibilita}
                      </p>
                    )}

                    {isSuperAdmin && offerta.reparti_nomi && offerta.reparti_nomi.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-3">
                        <Eye className="w-3 h-3 text-slate-400 mt-0.5" />
                        {offerta.reparti_nomi.map((nome, idx) => (
                          <Badge key={`reparto-${nome}-${idx}`} variant="outline" className="text-[10px] border-blue-300 text-blue-600">
                            {nome}
                          </Badge>
                        ))}
                      </div>
                    )}

                    <div className="flex items-end justify-between">
                      <div>
                        {minQ ? (
                          <>
                            <p className="text-xs text-slate-500">Da</p>
                            <p className="text-xl font-bold text-amber-600">
                              {'\u20AC'}{minQ.canone.toLocaleString('it-IT')}<span className="text-sm font-normal">/mese</span>
                            </p>
                            <p className="text-xs text-amber-600/70 font-medium">
                              {minQ.durata} mesi / {parseInt(minQ.km).toLocaleString('it-IT')} km
                            </p>
                          </>
                        ) : (
                          <p className="text-sm text-slate-400">Canoni da definire</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        className="bg-amber-500 hover:bg-amber-600 text-white btn-scale"
                        onClick={() => navigate(`/dashboard/offerta-mese/${offerta.id}`)}
                        data-testid={`btn-dettagli-${offerta.id}`}
                      >
                        Dettagli <ArrowRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Conferma Eliminazione</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">
            Vuoi eliminare l'offerta <strong>{deleteConfirm?.marca} {deleteConfirm?.modello}</strong>?
          </p>
          <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200">
            Tutte le offerte ancora aperte collegate a questo veicolo verranno automaticamente marcate come <strong>VENDUTA</strong>.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annulla</Button>
            <Button
              variant="destructive"
              onClick={() => handleDelete(deleteConfirm.id)}
              data-testid="btn-confirm-delete"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Elimina
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create/Edit Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Plus Jakarta Sans' }}>
              {editingOfferta ? 'Modifica Offerta' : 'Nuova Macchina - Offerta del Mese'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Dati Veicolo */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Car className="w-4 h-4" /> Dati Veicolo
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Marca *</Label>
                  <Input value={formData.marca} onChange={e => handleChange('marca', e.target.value)} placeholder="Es. FIAT" data-testid="input-marca" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Modello *</Label>
                  <Input value={formData.modello} onChange={e => handleChange('modello', e.target.value)} placeholder="Es. 500e" data-testid="input-modello" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Versione</Label>
                  <Input value={formData.versione} onChange={e => handleChange('versione', e.target.value)} placeholder="Es. Action 23,8 kWh" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Colore</Label>
                  <Input value={formData.colore} onChange={e => handleChange('colore', e.target.value)} placeholder="Es. Bianco" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Alimentazione</Label>
                  <Input value={formData.alimentazione} onChange={e => handleChange('alimentazione', e.target.value)} placeholder="Es. Elettrica" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Cambio</Label>
                  <Input value={formData.cambio} onChange={e => handleChange('cambio', e.target.value)} placeholder="Es. Automatico" />
                </div>
              </div>
            </div>

            {/* Dati Gestionali */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Dati Gestionali
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Company</Label>
                  <Input value={formData.company} onChange={e => handleChange('company', e.target.value)} placeholder="Es. Ayv, Scr, Lsy" data-testid="input-company" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Listino</Label>
                  <Input value={formData.listino} onChange={e => handleChange('listino', e.target.value)} placeholder="Es. NUOVO, USATA, CHtec" data-testid="input-listino" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Disponibilit{'\u00E0'}</Label>
                  <Input type="date" value={formData.data_disponibilita} onChange={e => handleChange('data_disponibilita', e.target.value)} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Data Immatricolazione</Label>
                  <Input type="date" value={formData.data_immatricolazione} onChange={e => handleChange('data_immatricolazione', e.target.value)} />
                </div>
              </div>
            </div>

            {/* Franchigie */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" /> Franchigie Assicurative
              </h3>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">RCA</Label>
                  <Input value={formData.franchigia_rca} onChange={e => handleChange('franchigia_rca', e.target.value)} placeholder="Es. 500 o 10%" data-testid="input-franchigia-rca" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Danni</Label>
                  <Input value={formData.franchigia_danni} onChange={e => handleChange('franchigia_danni', e.target.value)} placeholder="Es. 10%" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Furto</Label>
                  <Input value={formData.franchigia_furto} onChange={e => handleChange('franchigia_furto', e.target.value)} placeholder="Es. 15%" />
                </div>
              </div>
            </div>

            {/* Provvigione */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Banknote className="w-4 h-4" /> Provvigione
              </h3>
              <div className="max-w-xs space-y-1">
                <Label className="text-xs">Provvigione fissa per operazione ({'\u20AC'})</Label>
                <Input
                  type="number"
                  value={formData.provvigione_fissa}
                  onChange={e => handleChange('provvigione_fissa', e.target.value)}
                  placeholder="Es. 1000"
                  data-testid="input-provvigione"
                />
              </div>
            </div>

            {/* Matrice Combinazioni - FLESSIBILE */}
            <div>
              <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" /> Matrice Combinazioni (Canone mensile {'\u20AC'})
              </h3>
              <p className="text-xs text-slate-500 mb-3">
                Puoi personalizzare durate e km. I campi vuoti non saranno selezionabili dall'agente.
              </p>

              {/* Intestazioni Km (colonne) */}
              <div className="mb-3">
                <Label className="text-xs font-medium text-slate-600 mb-1 block">Colonne (Km)</Label>
                <div className="flex flex-wrap gap-2 items-center">
                  {formData.custom_km_options.map((km, idx) => (
                    <div key={idx} className="flex items-center gap-1">
                      <Input
                        value={km}
                        onChange={e => handleKmChange(idx, e.target.value)}
                        placeholder="Km"
                        className="h-8 w-24 text-xs text-center"
                        data-testid={`input-km-header-${idx}`}
                      />
                      {formData.custom_km_options.length > 1 && (
                        <button onClick={() => removeKm(idx)} className="text-red-400 hover:text-red-600">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" className="h-8 text-xs" onClick={addKm}>
                    <Plus className="w-3 h-3 mr-1" /> Km
                  </Button>
                </div>
              </div>

              {/* Tabella matrice */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr>
                      <th className="p-2 text-left text-slate-600 bg-slate-50 border border-slate-200 w-28">
                        Durata (mesi)
                      </th>
                      {formData.custom_km_options.map((km, idx) => (
                        <th key={idx} className="p-2 text-center text-slate-600 bg-slate-50 border border-slate-200 text-xs">
                          {km ? `${parseInt(km).toLocaleString('it-IT')} km` : '-'}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {formData.custom_durations.map((durata, dIdx) => (
                      <tr key={dIdx}>
                        <td className="p-1 bg-slate-50 border border-slate-200">
                          <div className="flex items-center gap-1">
                            <Input
                              value={durata}
                              onChange={e => handleDurationChange(dIdx, e.target.value)}
                              placeholder="Mesi"
                              className="h-8 w-20 text-xs text-center font-medium"
                              data-testid={`input-dur-header-${dIdx}`}
                            />
                            {formData.custom_durations.length > 1 && (
                              <button onClick={() => removeDuration(dIdx)} className="text-red-400 hover:text-red-600">
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                        {formData.custom_km_options.map((km, kIdx) => {
                          const key = `${durata}_${km}`;
                          return (
                            <td key={kIdx} className="p-1 border border-slate-200">
                              <Input
                                type="number"
                                value={formData.quotations[key] || ''}
                                onChange={e => handleQuotationChange(durata, km, e.target.value)}
                                placeholder="-"
                                className="h-8 text-center text-xs"
                                data-testid={`input-quota-${durata}-${km}`}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Button type="button" variant="outline" size="sm" className="mt-2 text-xs" onClick={addDuration}>
                <Plus className="w-3 h-3 mr-1" /> Aggiungi Durata
              </Button>
            </div>

            {/* Link Immagine */}
            <div className="space-y-1">
              <Label className="text-xs">Link Immagine (opzionale)</Label>
              <Input
                value={formData.link_immagine}
                onChange={e => handleChange('link_immagine', e.target.value)}
                placeholder="https://..."
              />
            </div>

            {/* Note */}
            <div className="space-y-1">
              <Label className="text-xs">Note</Label>
              <Textarea
                value={formData.note}
                onChange={e => handleChange('note', e.target.value)}
                placeholder="Note aggiuntive..."
                className="h-20"
              />
            </div>

            {/* Visibilit{'\u00E0'} Reparti */}
            {repartiDisponibili.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Eye className="w-4 h-4" /> Visibilit{'\u00E0'} Reparti
                </h3>
                <div className="flex items-center gap-2 mb-2">
                  <Checkbox
                    checked={formData.reparti_visibili.length === 0}
                    onCheckedChange={() => setFormData(prev => ({ ...prev, reparti_visibili: [] }))}
                  />
                  <span className="text-sm font-medium text-slate-700">Tutti i reparti</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {repartiDisponibili.map(reparto => (
                    <div key={reparto.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={formData.reparti_visibili.includes(reparto.id)}
                        onCheckedChange={() => handleRepartoToggle(reparto.id)}
                      />
                      <span className="text-sm text-slate-700">
                        {reparto.nome}
                        {reparto.is_sede_centrale && <span className="text-xs text-slate-400 ml-1">(Sede Centrale)</span>}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  {formData.reparti_visibili.length === 0
                    ? 'Visibile a tutti i reparti'
                    : `Visibile a ${formData.reparti_visibili.length} reparto/i`}
                </p>
              </div>
            )}

            {/* Toggle Pubblica nel Catalogo Generale */}
            {isSuperAdmin && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium text-blue-800">Pubblica nel Catalogo Generale</Label>
                    <p className="text-xs text-blue-600 mt-0.5">L'offerta apparirà anche nel catalogo veicoli (rispetta i permessi reparto)</p>
                  </div>
                  <Checkbox
                    checked={formData.pubblica_catalogo}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, pubblica_catalogo: !!checked }))}
                    data-testid="toggle-pubblica-catalogo"
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="bg-amber-500 hover:bg-amber-600"
                data-testid="btn-save-offerta"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {editingOfferta ? 'Salva Modifiche' : 'Crea Offerta'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OfferteDelMesePage;
